/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable  react-hooks/exhaustive-deps*/
import * as signalR from '@microsoft/signalr';
import React, { createContext, FC, ReactNode, useCallback, useEffect, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import useSettings from 'src/hooks/useSettings';
import { setSocketData } from 'src/slices/socket';
import { useDispatch, useSelector } from 'src/store';
import { SocketDataInterface } from 'src/types/socket';
import { appInsights, severityLevel } from 'src/utils/appInsight';

export enum ConnectionState {
	Connected = 'Connected',
	Disconnected = 'Disconnected',
	Connecting = 'Connecting',
	Reconnecting = 'Reconnecting',
}
interface SocketContextInterface {
	activeConnection: any | null;
	tunnelBoxData: any[];
	registerForTunnelEvents: Function;
	disconnectSocket: Function;
	clearTunnelBoxData: Function;
}

interface SocketProviderInterface {
	children: ReactNode;
}

const SocketContext = createContext<SocketContextInterface>({
	activeConnection: {},
	tunnelBoxData: null,
	registerForTunnelEvents: () => { },
	disconnectSocket: () => { },
	clearTunnelBoxData: () => { }
});

export const SocketProvider: FC<SocketProviderInterface> = ({ children }) => {
	const dispatch = useDispatch();
	const [activeConnection, setActiveConnection] = useState(null);
	const { isAuthenticated } = useAuth();
	const connectionStatus = activeConnection?.connection?.connectionState;
	const [tunnelBoxData, setTunnelBoxData] = useState<any[]>([]);
	const { settings } = useSettings();
	const { socketData }: SocketDataInterface = useSelector((state) => state.socket);

	const subscribeToTunnelEvents = () => {
		activeConnection.on('BoxItemAsync', (tunnel) => {
			setTunnelBoxData(getBoxItemRows(tunnel));
			appInsights.trackTrace({ message: 'SignalR: BoxItemAsync Subscribed', properties: { 'message': tunnel }, severityLevel: severityLevel.Information });
		});
	};

	const getBoxItemRows = (tunnelBoxData) => {
		if (tunnelBoxData?.boxItems?.length > 0) {
			return tunnelBoxData?.boxItems.map((bItem) => {
				return { boxName: bItem?.boxName, name: (bItem?.productId ? bItem?.name : 'Unknown'), description: bItem?.description, sku: bItem?.sku, quantity: bItem?.quantity, isEmpty: false, boxItemDetails: bItem.boxItemDetails };
			});
		}
		return [{ boxName: tunnelBoxData?.boxName, name: 'Empty Box', description: '', sku: '', quantity: 0, isEmpty: true, boxItemDetails: [] }];
	};

	const registerForTunnelEvents = async (tunnelId) => {
		if (tunnelId && activeConnection) {
			await activeConnection.send('RegisterTunnelAsync', tunnelId);
		}
	};

	const emitSocketEvent = async () => {
		appInsights.trackEvent({ name: 'emitSocketEvent', properties: { 'activeConnection': activeConnection } });
		await activeConnection.start()
			.then(async () => {
				setConnection(!!settings?.tunnelId);
				subscribeToTunnelEvents();
				registerForTunnelEvents(settings?.tunnelId);
			})
			.catch(error => {
				console.error('SignalR Connection failed: ', error);
				appInsights.trackException({ exception: error, severityLevel: severityLevel.Error });
			});
	};

	const setConnection = (isConnected: boolean) => {
		const socketDataVal = {
			isConnected: isConnected,
			socketData: socketData
		};
		dispatch(setSocketData(socketDataVal));
	}
	const clearTunnelBoxData = () => {
		setTunnelBoxData([]);
	}
	const initConnection = useCallback(() => {
		const accessToken: string = localStorage.getItem('accessToken');
		if (accessToken) {
			try {
				const makeConnection = new signalR.HubConnectionBuilder()
					.withUrl(`${process.env.REACT_APP_SOCKET_URL}tunnelHub`, { accessTokenFactory: () => localStorage.getItem('accessToken') })
					.withAutomaticReconnect({
						nextRetryDelayInMilliseconds: retryContext => {
							if (retryContext.previousRetryCount <= 10) {
								return 5000;
							} else {
								return 10000;
							}
						}
					})
					.build();
				setActiveConnection(makeConnection);
				appInsights.trackTrace({ message: 'Tunnel Hub Connected', properties: { 'activeConnection': activeConnection }, severityLevel: severityLevel.Information });
			} catch (e) {
				appInsights.trackException({ exception: e, properties: { 'activeConnection': activeConnection }, severityLevel: severityLevel.Error });
				return false;
			}
		} else {
			return false;
		}
	}, [useIsMountedRef]);

	const disconnectSocket = () => {
		activeConnection.off('BoxItemAsync');
		activeConnection.stop();
		setActiveConnection(null);
	};

	useEffect(() => {
		if (!!activeConnection) {
			activeConnection.onreconnected(async () => {
				subscribeToTunnelEvents();
				registerForTunnelEvents(settings?.tunnelId);
			});
		}
		return () => {
			if (!!activeConnection) {
				activeConnection.off('onreconnected');
			}
		};
	}, [activeConnection]);

	useEffect(() => {
		if (!activeConnection && isAuthenticated) {
			initConnection();
		}
		if (activeConnection) {
			return () => {
				disconnectSocket();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeConnection, isAuthenticated]);

	// Removed useEffect and moved the IF condition in to open. As it was previously working. 
	useEffect(() => {
		if (connectionStatus === 'Disconnected' && isAuthenticated) {
			emitSocketEvent();
		}
	}, [connectionStatus, isAuthenticated]);

	return (
		<SocketContext.Provider value={
			{
				activeConnection,
				tunnelBoxData,
				registerForTunnelEvents,
				disconnectSocket,
				clearTunnelBoxData
			}
		}>
			{children}
		</SocketContext.Provider>
	);
};

export default SocketContext;
